import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

export async function importCollectionRows(
  collectionName: string,
  rows: Array<Record<string, unknown>>
): Promise<string[]> {
  if (!rows.length) return [];

  const batch = writeBatch(db);
  const createdIds: string[] = [];
  rows.forEach((row) => {
    const ref = doc(collection(db, collectionName));
    createdIds.push(ref.id);
    batch.set(ref, {
      ...row,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
  return createdIds;
}

export interface ImportSchoolsResult {
  idsByRow: string[];
  importedCount: number;
  skippedCount: number;
}

/** Clé métier pour dédoublonner : même nom + même commune (insensible à la casse / espaces). */
function normalizeSchoolKey(name: unknown, commune: unknown): string {
  const collapse = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const normalizedName = collapse(String(name ?? ''));
  const normalizedCommune = collapse(String(commune ?? ''));
  return `${normalizedName}::${normalizedCommune}`;
}

/** Exportée pour le filtrage côté UI au chargement du CSV (écoles). */
export function schoolImportDedupeKey(row: Record<string, unknown>): string {
  return normalizeSchoolKey(row.name, row.commune);
}

/** Clé de dédoublonnage → id du document Firestore `schools`. */
export async function fetchExistingSchoolKeyToId(): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const existingSnap = await getDocs(collection(db, 'schools'));
  existingSnap.docs.forEach((d) => {
    const data = d.data() as Record<string, unknown>;
    const key = normalizeSchoolKey(data.name, data.commune);
    if (!map.has(key)) {
      map.set(key, d.id);
    }
  });
  return map;
}

const FIRESTORE_BATCH_MAX = 400;

export async function importSchoolsRowsUnique(
  rows: Array<Record<string, unknown>>
): Promise<ImportSchoolsResult> {
  if (!rows.length) {
    return { idsByRow: [], importedCount: 0, skippedCount: 0 };
  }

  const idsByRow = Array.from({ length: rows.length }, () => '');
  const existingKeyToId = await fetchExistingSchoolKeyToId();

  const pendingSets: Array<{ ref: ReturnType<typeof doc>; payload: Record<string, unknown> }> =
    [];

  let importedCount = 0;
  let skippedCount = 0;

  rows.forEach((row, index) => {
    const key = normalizeSchoolKey(row.name, row.commune);
    if (!String(row.name ?? '').trim() || existingKeyToId.has(key)) {
      skippedCount += 1;
      return;
    }

    const ref = doc(collection(db, 'schools'));
    idsByRow[index] = ref.id;
    existingKeyToId.set(key, ref.id);
    importedCount += 1;
    pendingSets.push({
      ref,
      payload: {
        ...row,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    });
  });

  for (let i = 0; i < pendingSets.length; i += FIRESTORE_BATCH_MAX) {
    const slice = pendingSets.slice(i, i + FIRESTORE_BATCH_MAX);
    const batch = writeBatch(db);
    slice.forEach(({ ref, payload }) => {
      batch.set(ref, payload);
    });
    await batch.commit();
  }

  return { idsByRow, importedCount, skippedCount };
}

const CANDIDATURES_COLLECTION = 'candidatures';

export function candidatureImportDedupeKey(row: Record<string, unknown>): string {
  const u = String(row.userId ?? '').trim().toLowerCase();
  const p = String(row.postId ?? '').trim().toLowerCase();
  return `${u}::${p}`;
}

export async function fetchExistingCandidatureKeyToId(): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const snap = await getDocs(collection(db, CANDIDATURES_COLLECTION));
  snap.docs.forEach((d) => {
    const data = d.data() as Record<string, unknown>;
    const key = candidatureImportDedupeKey(data);
    if (!key || key === '::') return;
    if (!map.has(key)) {
      map.set(key, d.id);
    }
  });
  return map;
}

export async function importCandidaturesRowsUnique(
  rows: Array<Record<string, unknown>>
): Promise<ImportSchoolsResult> {
  if (!rows.length) {
    return { idsByRow: [], importedCount: 0, skippedCount: 0 };
  }

  const idsByRow = Array.from({ length: rows.length }, () => '');
  const existingKeyToId = await fetchExistingCandidatureKeyToId();
  const pendingSets: Array<{ ref: ReturnType<typeof doc>; payload: Record<string, unknown> }> = [];
  let importedCount = 0;
  let skippedCount = 0;

  rows.forEach((row, index) => {
    const key = candidatureImportDedupeKey(row);
    if (!String(row.userId ?? '').trim() || !String(row.postId ?? '').trim() || existingKeyToId.has(key)) {
      skippedCount += 1;
      return;
    }
    const ref = doc(collection(db, CANDIDATURES_COLLECTION));
    idsByRow[index] = ref.id;
    existingKeyToId.set(key, ref.id);
    importedCount += 1;
    pendingSets.push({
      ref,
      payload: {
        ...row,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    });
  });

  for (let i = 0; i < pendingSets.length; i += FIRESTORE_BATCH_MAX) {
    const slice = pendingSets.slice(i, i + FIRESTORE_BATCH_MAX);
    const batch = writeBatch(db);
    slice.forEach(({ ref, payload }) => {
      batch.set(ref, payload);
    });
    await batch.commit();
  }

  return { idsByRow, importedCount, skippedCount };
}

const POSTS_COLLECTION = 'posts';

/** Jour calendaire en UTC (YYYY-MM-DD), cohérent entre CSV, Date client et Timestamp Firestore. */
const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function postCalendarDateKey(d: unknown): string {
  if (d == null || d === '') return '';
  if (typeof d === 'string') {
    const s = d.trim();
    const m = ISO_DATE_ONLY.exec(s);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  if (d instanceof Date && !Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }
  const secNs = d as { seconds?: unknown; nanoseconds?: unknown };
  if (typeof secNs.seconds === 'number' && Number.isFinite(secNs.seconds)) {
    const ms =
      secNs.seconds * 1000 +
      (typeof secNs.nanoseconds === 'number' && Number.isFinite(secNs.nanoseconds)
        ? secNs.nanoseconds / 1e6
        : 0);
    const t = new Date(ms);
    if (!Number.isNaN(t.getTime())) return t.toISOString().slice(0, 10);
  }
  const maybeTs = d as { toDate?: () => Date };
  if (typeof maybeTs.toDate === 'function') {
    const t = maybeTs.toDate();
    if (t instanceof Date && !Number.isNaN(t.getTime())) {
      return t.toISOString().slice(0, 10);
    }
  }
  if (typeof d === 'number' && Number.isFinite(d)) {
    const t = new Date(d);
    if (!Number.isNaN(t.getTime())) return t.toISOString().slice(0, 10);
  }
  const parsed = new Date(String(d));
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

function collapsePostTitle(s: string): string {
  return s
    .replace(/\u00a0/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Même école + même titre + même jour (UTC, YYYY-MM-DD), insensible à la casse / espaces.
 * Ne pas inclure le statut : deux statuts pour le même créneau restent une seule clé métier.
 */
export function postImportDedupeKey(row: Record<string, unknown>): string {
  const schoolId = String(row.schoolId ?? '').trim().toLowerCase();
  const title = collapsePostTitle(String(row.title ?? ''));
  const dateK = postCalendarDateKey(row.date);
  return `${schoolId}::${title}::${dateK}`;
}

export function postImportRowEligible(row: Record<string, unknown>): boolean {
  return (
    Boolean(String(row.schoolId ?? '').trim()) &&
    Boolean(String(row.title ?? '').trim()) &&
    Boolean(postCalendarDateKey(row.date))
  );
}

export type PostsImportExistingIndex = {
  keyToId: Map<string, string>;
  /** IDs de documents présents dans `posts` (le postId CSV doit correspondre à l’id du doc). */
  existingIds: Set<string>;
};

/** Une lecture Firestore : index métier + ensemble des ids de documents. */
export async function fetchExistingPostsImportIndex(): Promise<PostsImportExistingIndex> {
  const keyToId = new Map<string, string>();
  const existingIds = new Set<string>();
  const snap = await getDocs(collection(db, POSTS_COLLECTION));
  snap.docs.forEach((d) => {
    existingIds.add(d.id);
    const data = d.data() as Record<string, unknown>;
    if (!postImportRowEligible(data)) return;
    const key = postImportDedupeKey(data);
    if (!keyToId.has(key)) {
      keyToId.set(key, d.id);
    }
  });
  return { keyToId, existingIds };
}

export async function fetchExistingPostKeyToId(): Promise<Map<string, string>> {
  const { keyToId } = await fetchExistingPostsImportIndex();
  return keyToId;
}

function rowWithoutPostIdField(row: Record<string, unknown>): Record<string, unknown> {
  const { postId: _omit, ...rest } = row;
  return rest;
}

export async function importPostsRowsUnique(
  rows: Array<Record<string, unknown>>
): Promise<ImportSchoolsResult> {
  if (!rows.length) {
    return { idsByRow: [], importedCount: 0, skippedCount: 0 };
  }

  const idsByRow = Array.from({ length: rows.length }, () => '');
  const { keyToId: existingKeyToId, existingIds } = await fetchExistingPostsImportIndex();
  const pendingSets: Array<{ ref: ReturnType<typeof doc>; payload: Record<string, unknown> }> = [];
  /** postId CSV déjà traité dans ce lot (orphelins : pas encore dans existingIds). */
  const pendingCsvPostIds = new Set<string>();
  let importedCount = 0;
  let skippedCount = 0;

  rows.forEach((row, index) => {
    const csvPid = String(row.postId ?? '').trim();
    if (csvPid) {
      if (existingIds.has(csvPid) || pendingCsvPostIds.has(csvPid)) {
        skippedCount += 1;
        return;
      }
      if (!postImportRowEligible(row)) {
        skippedCount += 1;
        return;
      }
      const ref = doc(collection(db, POSTS_COLLECTION));
      idsByRow[index] = ref.id;
      pendingCsvPostIds.add(csvPid);
      importedCount += 1;
      pendingSets.push({
        ref,
        payload: {
          ...rowWithoutPostIdField(row),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
      });
      return;
    }
    const key = postImportDedupeKey(row);
    if (!postImportRowEligible(row) || existingKeyToId.has(key)) {
      skippedCount += 1;
      return;
    }
    const ref = doc(collection(db, POSTS_COLLECTION));
    idsByRow[index] = ref.id;
    existingKeyToId.set(key, ref.id);
    importedCount += 1;
    pendingSets.push({
      ref,
      payload: {
        ...rowWithoutPostIdField(row),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    });
  });

  for (let i = 0; i < pendingSets.length; i += FIRESTORE_BATCH_MAX) {
    const slice = pendingSets.slice(i, i + FIRESTORE_BATCH_MAX);
    const batch = writeBatch(db);
    slice.forEach(({ ref, payload }) => {
      batch.set(ref, payload);
    });
    await batch.commit();
  }

  return { idsByRow, importedCount, skippedCount };
}

